import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { SkeletonBlockComponent } from '@frontendlib/components/components/skeleton-block/skeleton-block.component';
import { FORM_CONTRACTED_WIDTH } from '@modules/common/components/full-form-base/form-contracted-width';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { ResizeDirective } from '@modules/utils/directives/resize/resize.directive';

import type { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';

/** The reader's header icons before its workspace chip and Assign, folded away in a contracted pane. */
const REPLY_ACTIONS = ['reply', 'reply-all', 'forward'] as const;

/** The reader's header icons after its workspace chip and Assign; Share is folded away in a contracted pane. */
const TRAILING_ACTIONS = ['share', 'more', 'close'] as const;
const CONTRACTED_TRAILING_ACTIONS = ['more', 'close'] as const;

/** Email, Attachments, Stitch, Comments: each tab's width and its label bar's. */
const TABS = [
  { width: 66, bar: 34 },
  { width: 111, bar: 79 },
  { width: 66, bar: 34 },
  { width: 98, bar: 66 },
];
const ACTIVITY_TAB = { width: 77, bar: 45 };

/**
 * The email reader's skeleton, shown in panel 3 from the click until the message has been read (Pavel 2026-10-28:
 * "we need to show maybe some skeleton placeholder, ideally UI should not jump").
 *
 * Every box is the loaded reader's (`message-form.component.less`, measured on stitch.bytecrate.me at 1920x1080):
 * the 64px sender heading with its 40px avatar and two 24px lines, the action icons and the mailbox badge, the
 * 40px subject row, the 42px tab row, the Knowledge box (8px / 12px round it, three rows, closed by a rule) and the
 * body. So the subject, the sender row and the top of the body sit where the reader puts them and nothing moves when
 * it arrives. The subject and date are the real ones, from the list row.
 *
 * Every block is a `stch-skeleton-block` shaped with Tailwind: Gray/200 with the medium corner, with the shared block's
 * default pulse, like every other skeleton in the app (Pavel 2026-10-28). The bars have fixed widths, never the length
 * of what they stand for. The Knowledge box is drawn the way the reader's own box looks while its rows load (Blue/50
 * card, row icons, the rows' own loading chips), so it does not change when the reader takes over; its chips pulse too.
 */
@Component({
  selector: 'app-mail-reader-skeleton',
  templateUrl: './mail-reader-skeleton.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DateFormatPipe, IconComponent, ResizeDirective, SkeletonBlockComponent],
  host: { class: 'flex h-full flex-col overflow-hidden bg-white' },
})
export class MailReaderSkeletonComponent {
  /** The row the click opened: its subject and date are shown as they are. */
  public readonly message = input.required<MessageSummaryDto>();

  /** Whether the reader will show its Knowledge box (the user's own Knowledge toggle), so its space is held. */
  public readonly knowledge = input(true);

  /**
   * Which team mailbox the email is in (`itemWorkspace`): the reader then leads its header with the workspace chip
   * on a 32px row, and puts the team label by the date of a team-mailbox email.
   */
  public readonly workspace = input<'team' | 'mine' | undefined>(undefined);

  /** Whether the reader offers the hand-over: the Assign button in the header and the Responsible row under the tabs. */
  public readonly handover = input(false);

  /**
   * Whether the pane is contracted, decided as the reader decides it (`FullFormBaseComponent.handleResize`): the
   * sender row, the reader's `.heading`'s box, narrower than `FORM_CONTRACTED_WIDTH`. Contracted until measured, as
   * the reader starts. A hand-over adds Assign and changes no icon.
   */
  protected readonly contracted = signal(true);

  /** The icons before the workspace chip and Assign: Reply, Reply all, Forward, none in a contracted pane. */
  protected readonly leadingActions = computed(() => (this.contracted() ? [] : REPLY_ACTIONS));

  /** The icons after them: Share, More, Close, or More and Close in a contracted pane. */
  protected readonly trailingActions = computed(() =>
    this.contracted() ? CONTRACTED_TRAILING_ACTIONS : TRAILING_ACTIONS,
  );

  /** Whether the reader adds its Activity tab after Comments (`itemActivityTenant`: an email of the team on screen). */
  public readonly activity = input(false);

  /**
   * The reader's tabs (Email, Attachments, Stitch, Comments, and Activity when it has one): each tab's width and its
   * label bar's, 4px under the label's.
   */
  protected readonly tabs = computed(() => (this.activity() ? [...TABS, ACTIVITY_TAB] : TABS));

  /**
   * Knots, Tags, Connections, with the chips each row draws while it loads: the classes of the `stch-skeleton-block`s
   * in `knots.component.html`, `tags.component.html` and `connections.component.html` (#2530), exactly.
   */
  protected readonly knowledgeRows: ReadonlyArray<{ icon: Icon; chips: string[] }> = [
    {
      icon: 'sapphire-knots',
      chips: [
        'mr-2 inline-block h-5 w-32 rounded-full align-middle bg-blue-200/60',
        'inline-block h-5 w-24 rounded-full align-middle bg-blue-200/60',
      ],
    },
    {
      icon: 'sapphire-tags',
      chips: [
        'mr-2 inline-block h-5 w-20 rounded-full align-middle bg-blue-200/60',
        'mr-2 inline-block h-5 w-28 rounded-full align-middle bg-blue-200/60',
        'inline-block h-5 w-16 rounded-full align-middle bg-blue-200/60',
      ],
    },
    {
      icon: 'sapphire-contact',
      chips: ['inline-block h-5 w-40 rounded-full align-middle bg-blue-200/60'],
    },
  ];

  /** The body's text lines, as a share of the body's width; `null` is the gap between two paragraphs. */
  protected readonly bodyLines = ['92%', '86%', '95%', '58%', null, '90%', '82%', '40%', null, '74%', '30%'];

  /** The sender row's size, reported by `ResizeDirective` as the reader's heading's is. */
  protected handleResize([width]: [number, number]): void {
    this.contracted.set(width < FORM_CONTRACTED_WIDTH);
  }
}
